import { useFormikContext } from 'formik';
import { useCallback } from 'react';

/**
 * `setFieldValue` from Formik isn't type-safe.
 * This function wraps its functionality, enabling
 * type safety when a generic type argument is provided
 * for the form values type.
 *
 * ### Example
 * ```typescript
 * const setFieldValue = useSetFormikFieldValue<{ name: string; age: number; }>();
 * setFieldValue('age', 138); // When entering "age", TS knows that the value must be a number
 * ```
 */
export function useSetFormikFieldValue<T>() {
  const { setFieldValue } = useFormikContext<T>();
  const setValue = useCallback(
    <K extends keyof T>(fieldName: K, fieldValue: T[K]) => {
      if (typeof fieldName !== 'string') {
        throw new Error('Field name must be a string');
      }
      setFieldValue(fieldName, fieldValue);
    },
    [setFieldValue]
  );
  return setValue;
}
